import { PrimitiveValueObject } from './primitiveValueObject';

import * as utils from '@/utils/date';

/**
 * 単一項目のValueObject.
 */
export abstract class PrimitiveValueObjectDateTime extends PrimitiveValueObject<Date> {
  /**
   * 表示用日時の文字列変換
   * @param date 日時
   * @param isShowSeconds 秒を表示するか
   * @returns yyyy-MM-dd hh:mm:ss
   */
  static toStringDateTimeDisplay(date: Date, isShowSeconds = true) {
    const ymd = PrimitiveValueObjectDateTime.toStringDateDisplay(date);
    const hms = PrimitiveValueObjectDateTime.toStringTimeDisplay(date, isShowSeconds);
    return `${ymd} ${hms}`;
  }

  /**
   * 日時の文字列変換
   * @param date 日時
   * @returns yyyy-MM-ddThh:mm:ss.zzzZ
   */
  static toStringDateTime(date: Date) {
    const ymd = PrimitiveValueObjectDateTime.toStringDate(date);
    const hms = PrimitiveValueObjectDateTime.toStringTime(date);
    const milli = (`000${date.getTime() % 1000}`).slice(-3);
    return `${ymd}T${hms}.${milli}Z`;
  }

  /**
   * 日付文字列変換
   * @param date 日時
   * @param isShowWeek 週を表示するか
   * @param isShowYear 年を表示するか
   * @returns 文字列
   */
  static toStringDateDisplay(date: Date, isShowWeek = true, isShowYear = true) {
    return utils.toStringDateDisplay(date, isShowWeek, isShowYear);
  }

  /**
   * 日付文字列変換
   * @param date 日時
   * @returns 日時文字列
   */
  static toStringDate(date: Date) {
    return utils.toStringDate(date);
  }

  /**
   * 時間文字列変換
   * @param date 日時
   * @param isShowSeconds 秒を表示するか
   * @returns 文字列
   */
  static toStringTimeDisplay(date: Date, isShowSeconds = true) {
    return utils.toStringTimeDisplay(date, isShowSeconds);
  }

  /**
   * 時間文字列変換
   * @param date 日時
   * @param isShowSeconds 秒を表示するか
   * @returns 文字列
   */
  static toStringTime(date: Date, isShowSeconds = true) {
    return utils.toStringTime(date, isShowSeconds);
  }

  /**
   * 現時刻をパースする
   * @param time 時間の文字
   * @returns 日時
   */
  static parseTimeByCurrentDate(time: string, date: Date | undefined = undefined) {
    return utils.parseTimeByCurrentDate(time, date);
  }

  /**
   * 日時表示文字列を取得
   * @param isShowSeconds 秒を表示するか
   * @returns 文字列
   */
  toStringDateTimeDisplay(isShowSeconds = true) {
    return PrimitiveValueObjectDateTime.toStringDateTimeDisplay(this.value, isShowSeconds);
  }

  /**
   * 日付表示文字列を取得
   * @param isShowWeek 週を表示するか
   * @param isShowYear 年を表示するか
   * @returns 文字列
   */
  toStringDateDisplay(isShowWeek = true, isShowYear = true) {
    return PrimitiveValueObjectDateTime.toStringDateDisplay(this.value, isShowWeek, isShowYear);
  }

  /**
   * 時間表示文字列取得
   * @param isShowSeconds 秒を表示するか
   * @returns 文字列
   */
  toStringTimeDisplay(isShowSeconds = true) {
    return PrimitiveValueObjectDateTime.toStringTimeDisplay(this.value, isShowSeconds);
  }

  /**
   * 日時文字列を取得
   * @returns 文字列
   */
  toStringDateTime() {
    return PrimitiveValueObjectDateTime.toStringDateTime(this.value);
  }

  /**
   * 日付文字列を取得
   * @returns 文字列
   */
  toStringDate() {
    return PrimitiveValueObjectDateTime.toStringDate(this.value);
  }

  /**
   * 時間文字列を取得
   * @returns 文字列
   */
  toStringTime() {
    return PrimitiveValueObjectDateTime.toStringTime(this.value);
  }

  /**
   * toStringShort
   * @returns 文字
   */
  toStringShort() {
    const now = new Date();
    if (now.toDateString() === this.value.toDateString()) {
      return PrimitiveValueObjectDateTime.toStringTimeDisplay(this.value, false);
    }
    if (now.getFullYear() === this.value.getFullYear()) {
      return PrimitiveValueObjectDateTime.toStringDateDisplay(this.value, false, false);
    }
    return PrimitiveValueObjectDateTime.toStringDateDisplay(this.value, false, true);
  }
}
